// outputs/active-adapter-proposal-2026-10-21/src/tcou-purchase-resolver.mjs
var TAG = "TCOU888-20";
function amazonDestination(raw) {
  if (typeof raw !== "string" || !raw || raw !== raw.trim() || /[^\x21-\x7e]|\\/.test(raw)) return null;
  if (!/^https:\/\/(?:www\.)?amazon\.com\/dp\/[A-Z0-9]{10}\/?(?:\?[^#]*)?$/.test(raw)) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:" || !["amazon.com", "www.amazon.com"].includes(u.hostname) || u.username || u.password || u.port || u.hash || !/^\/dp\/[A-Z0-9]{10}\/?$/.test(u.pathname)) return null;
    const pairs = [...u.searchParams];
    if (new Set(pairs.map((x) => x[0])).size !== pairs.length || pairs.some(([k, v]) => !["language", "th", "psc", "tag"].includes(k) || !v)) return null;
    const tag = u.searchParams.get("tag");
    if (tag && tag !== TAG) return null;
    if (!tag) u.searchParams.set("tag", TAG);
    return tag ? raw : u.href;
  } catch {
    return null;
  }
}

// outputs/active-adapter-proposal-2026-10-21/src/tcou-storefront-components.mjs
function createCrossSellController({ loadCurrentProduct, addVariant, readCart, onCart }) {
  let busy = false;
  return { get busy() {
    return busy;
  }, async add(productId, variantId) {
    if (busy) return { accepted: false, reason: "busy" };
    busy = true;
    let attempted = false;
    try {
      const p = await loadCurrentProduct(productId);
      const v = p?.variants?.find((v2) => v2.id === variantId);
      if (p?.id !== productId || p.mode !== "Shopify" || p.approved !== true || p.externalEvidence === true || !v || v.available !== true || v.decision?.local_allowed !== true)
        return { accepted: false, reason: "not_current_approved_ordinary" };
      attempted = true;
      await addVariant({ id: variantId, quantity: 1 });
      return { accepted: true, variantId, sku: v.sku, price: v.price };
    } finally {
      try {
        if (attempted) onCart(await readCart());
      } finally {
        busy = false;
      }
    }
  } };
}
function mountCrossSell(root, products, controller) {
  const doc = root.ownerDocument;
  root.replaceChildren();
  for (const p of products) {
    let selected = function() {
      return p.variants.find((v) => v.id === select.value);
    }, update = function() {
      const v = selected();
      details.textContent = v ? `SKU: ${v.sku || "not supplied"} \xB7 ${v.priceDisplay || "Price unavailable"}` : "Choose a variant";
      button.disabled = p.approved !== true || v?.available !== true || v?.decision?.local_allowed !== true;
    };
    if (p.mode !== "Shopify" || p.externalEvidence === true) continue;
    const box = doc.createElement("article");
    box.className = "tcou-find";
    const h = doc.createElement("h3");
    h.textContent = p.title;
    box.append(h);
    const select = doc.createElement("select");
    select.setAttribute("aria-label", p.title + " variant");
    for (const v of p.variants) {
      const o = doc.createElement("option");
      o.value = v.id;
      o.textContent = v.title;
      select.append(o);
    }
    const details = doc.createElement("p");
    details.setAttribute("aria-live", "polite");
    const status = doc.createElement("p");
    status.setAttribute("role", "status");
    const button = doc.createElement("button");
    button.type = "button";
    button.textContent = "Add selected option";
    select.addEventListener("change", update);
    button.addEventListener("click", async () => {
      button.disabled = true;
      status.textContent = "Updating cart\u2026";
      try {
        const result = await controller.add(p.id, select.value);
        status.textContent = result.accepted ? "Selected option added; cart refreshed." : "Item unavailable for this offer.";
      } catch {
        status.textContent = "Could not confirm addition. Check the refreshed cart before retrying.";
      } finally {
        update();
      }
    });
    box.append(select, details, button, status);
    update();
    root.append(box);
  }
}

// outputs/active-adapter-proposal-2026-10-21/src/tcou-cart-integration.mjs
function ajaxVariantId(gid) {
  if (typeof gid === "number" && !Number.isSafeInteger(gid)) throw Error("Unsafe numeric identity");
  const value = String(gid).replace(/^gid:\/\/shopify\/ProductVariant\//, "");
  if (!/^[1-9][0-9]*$/.test(value)) throw Error("Invalid variant identity");
  return value;
}
function quantity(value, allowZero = false) {
  if (!Number.isSafeInteger(value) || value < (allowZero ? 0 : 1)) throw Error("Invalid quantity");
  return value;
}
function createAjaxCartTransport({ fetchImpl, root = "/", writesEnabled = false }) {
  if (!/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(root)) throw Error("Use a relative localized storefront root");
  async function request(path, body) {
    if (body && !writesEnabled) throw Error("Live cart writes are disabled");
    const response = await fetchImpl(root + path, {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json", ...body ? { "Content-Type": "application/json" } : {} },
      ...body ? { body: JSON.stringify(body) } : {}
    });
    let result;
    try {
      result = await response.json();
    } catch {
      throw Error("Unreadable cart response; refresh required");
    }
    if (!response.ok) {
      const error = Error(result.description || result.message || "Cart request failed");
      error.status = response.status;
      error.cartResponse = result;
      throw error;
    }
    return result;
  }
  return {
    read: () => request("cart.js"),
    add: ({ id, quantity: q }) => request("cart/add.js", { items: [{ id: ajaxVariantId(id), quantity: quantity(q) }] }),
    change: ({ key, quantity: q }) => {
      if (typeof key !== "string" || !key) throw Error("Exact cart line key required");
      return request("cart/change.js", { id: key, quantity: quantity(q, true) });
    }
  };
}
function createOrdinaryCartIntegration({ transport, loadCurrentProduct, approvedProductIDs, onCart }) {
  const allowlist = new Set(approvedProductIDs);
  let selectedQuantity = 1, lastCart = null, refreshRequired = false, mutationAttempted = false, working = false;
  async function refresh() {
    const cart = await transport.read();
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || typeof cart.total_price !== "number")
      throw Error("Invalid server cart; do not display estimated totals");
    lastCart = cart;
    await onCart(cart);
    refreshRequired = false;
    return cart;
  }
  async function load(id) {
    if (!allowlist.has(id)) return null;
    const p = await loadCurrentProduct(id);
    return p?.id === id && p.mode === "Shopify" && p.approved === true && p.externalEvidence === false ? p : null;
  }
  const base = createCrossSellController({
    loadCurrentProduct: load,
    addVariant: async (item) => {
      mutationAttempted = true;
      return transport.add({ ...item, quantity: selectedQuantity });
    },
    readCart: async () => {
      try {
        return await refresh();
      } catch (error) {
        refreshRequired = true;
        throw error;
      }
    },
    onCart: () => {
    }
  });
  return {
    get cart() {
      return lastCart;
    },
    get busy() {
      return working || base.busy;
    },
    get refreshRequired() {
      return refreshRequired;
    },
    async recover() {
      if (working) throw Error("Cart request in progress");
      working = true;
      try {
        return await refresh();
      } catch (error) {
        refreshRequired = true;
        throw error;
      } finally {
        working = false;
      }
    },
    async add(productId, variantId, q = 1) {
      if (working || base.busy) return { accepted: false, reason: "busy" };
      if (refreshRequired) return { accepted: false, reason: "refresh_required" };
      quantity(q);
      selectedQuantity = q;
      mutationAttempted = false;
      working = true;
      try {
        return await base.add(productId, variantId);
      } catch (error) {
        return {
          accepted: false,
          reason: "request_not_confirmed",
          message: error.message,
          mutationMayHaveOccurred: mutationAttempted,
          cart: lastCart,
          refreshRequired
        };
      } finally {
        working = false;
      }
    },
    async change(lineKey, q) {
      quantity(q, true);
      if (working || base.busy) return { accepted: false, reason: "busy" };
      if (refreshRequired) return { accepted: false, reason: "refresh_required" };
      working = true;
      let attempted = false;
      try {
        const cart = await refresh();
        const line = cart.items.find((x) => x.key === lineKey);
        if (!line) return { accepted: false, reason: "line_changed" };
        if (q > 0) {
          const id = "gid://shopify/Product/" + line.product_id;
          const variant = "gid://shopify/ProductVariant/" + line.variant_id;
          const p = await load(id);
          const v = p?.variants?.find((v2) => v2.id === variant);
          if (!v || v.available !== true || v.decision?.local_allowed !== true)
            return { accepted: false, reason: "not_current_approved_ordinary" };
        }
        attempted = true;
        await transport.change({ key: lineKey, quantity: q });
        return { accepted: true };
      } catch (error) {
        return { accepted: false, reason: "change_not_confirmed", message: error.message };
      } finally {
        try {
          if (attempted) await refresh();
        } catch {
          refreshRequired = true;
          return { accepted: false, reason: "refresh_required", mutationMayHaveOccurred: true };
        } finally {
          working = false;
        }
      }
    }
  };
}
function currentOfferSelection(product, saved = null) {
  const variantId = saved ? product.variants.some((v) => v.id === saved.variantId) ? saved.variantId : "" : product.variants.find((v) => v.available === true)?.id || "";
  return { variantId, quantity: Number.isSafeInteger(saved?.quantity) && saved.quantity > 0 ? saved.quantity : 1 };
}
function mountQuantityCrossSell(root, products, integration, state = /* @__PURE__ */ new Map()) {
  const controls = /* @__PURE__ */ new Map();
  mountCrossSell(root, products, {
    get busy() {
      return integration.busy;
    },
    add: (productId, variantId) => integration.add(productId, variantId, controls.get(productId)?.valueAsNumber ?? 1)
  });
  const displayed = products.filter((p) => p.mode === "Shopify" && p.externalEvidence !== true);
  for (const [index, p] of displayed.entries()) {
    const box = root.children[index], label = root.ownerDocument.createElement("label");
    label.textContent = "Quantity";
    const input = root.ownerDocument.createElement("input");
    input.type = "number";
    input.min = "1";
    input.step = "1";
    const selection = currentOfferSelection(p, state.get(p.id));
    input.value = String(selection.quantity);
    input.setAttribute("aria-label", p.title + " offer quantity");
    label.append(input);
    box.insertBefore(label, box.querySelector("button"));
    controls.set(p.id, input);
    const select = box.querySelector("select");
    select.value = selection.variantId;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    const remember = () => state.set(p.id, { variantId: select.value, quantity: input.valueAsNumber });
    select.addEventListener("change", remember);
    input.addEventListener("input", remember);
    remember();
  }
  return state;
}

// outputs/active-adapter-proposal-2026-10-21/src/tcou-integration.mjs
function parseContract(html, selector, Parser = DOMParser) {
  const doc = new Parser().parseFromString(html, "text/html");
  const node = doc.querySelector(selector);
  if (!node) throw Error("Missing authoritative theme contract");
  return JSON.parse(node.textContent);
}
function ownedFormAllowed(form) {
  const scope = form.closest("[data-tcou-purchase]");
  return !!scope && !scope.inert && scope.dataset.tcouMode === "Shopify" && String(form.querySelector('[name="id"]')?.value || "") === String(scope.dataset.tcouVariant || "") && !!scope.dataset.tcouVariant;
}
function contractURL(root, path, section) {
  const url = new URL(path, location.origin);
  if (url.origin !== location.origin) throw Error("Cross-origin contract");
  if (!url.pathname.startsWith(root)) throw Error("Non-localized contract");
  url.searchParams.set("section_id", section);
  return url;
}
async function fetchContract(url, selector) {
  const r = await fetch(url, { credentials: "same-origin", cache: "no-store", headers: { Accept: "text/html" } });
  if (!r.ok) throw Error("Contract unavailable");
  return parseContract(await r.text(), selector);
}
function boot(doc = document) {
  const root = window.Shopify?.routes?.root;
  if (!/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(root || "")) return;
  const cartURL = root + "cart";
  const sectionFor = (e) => e.closest(".shopify-section") || e;
  const pending = /* @__PURE__ */ new WeakMap(), consented = /* @__PURE__ */ new WeakSet(), mounts = /* @__PURE__ */ new WeakSet(), saved = /* @__PURE__ */ new Map();
  function setPending(section, busy, failed = false) {
    section.querySelectorAll("[data-tcou-purchase],[data-tcou-sticky]").forEach((el) => {
      el.inert = busy;
      el.toggleAttribute("inert", busy);
      el.setAttribute("aria-busy", String(busy));
    });
    let status = section.querySelector("[data-tcou-variant-status]");
    if (!status) {
      status = doc.createElement("p");
      status.dataset.tcouVariantStatus = "";
      status.setAttribute("role", "status");
      section.querySelector(".product-form")?.append(status);
    }
    if (status) status.textContent = failed ? "Unable to confirm this option. Refresh before purchasing." : busy ? "Checking selected option\xC3\u0192\xC6\u2019\xC3\u201A\xC2\xA2\xC3\u0192\xC2\xA2\xC3\xA2\xE2\u201A\xAC\xC5\xA1\xC3\u201A\xC2\xAC\xC3\u0192\xE2\u20AC\u0161\xC3\u201A\xC2\xA6" : "";
  }
  doc.addEventListener("tcou:variant-pending", (e) => {
    const section = sectionFor(e.target);
    pending.set(section, { token: e.detail?.token, busy: true });
    setPending(section, true);
  });
  doc.addEventListener("tcou:variant-settled", (e) => {
    const section = sectionFor(e.target);
    if (pending.get(section)?.token !== e.detail?.token) return;
    pending.set(section, { token: e.detail?.token, busy: !e.detail?.applied });
    setPending(section, !e.detail?.applied, !e.detail?.applied);
    updateSticky();
  });
  doc.addEventListener("click", (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    if (a.closest("[data-tcou-purchase],[data-tcou-sticky]")) {
      if (pending.get(sectionFor(a))?.busy || a.closest("[inert]")) {
        e.preventDefault();
        return;
      }
      if (a.matches(".tcou-amazon-link") && !amazonDestination(a.href)) e.preventDefault();
    }
    const line = a.closest("[data-tcou-line-mode]");
    if (line && line.dataset.tcouLineMode !== "Shopify" && a.matches(".quantity-up")) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);
  doc.addEventListener("change", (e) => {
    const line = e.target.closest("[data-tcou-line-mode]");
    if (line && line.dataset.tcouLineMode !== "Shopify" && e.target.matches(".cart-item__quantity-input") && Number(e.target.value) > Number(line.dataset.tcouLineQuantity)) {
      e.target.value = line.dataset.tcouLineQuantity;
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);
  const termsAccepted = (surface) => [...surface.querySelectorAll("terms-agreement")].every((el) => !!el.querySelector("input:checked"));
  const freshCart = async () => {
    const c = await fetchContract(contractURL(root, root, "tcou-cart-contract"), "[data-tcou-cart-contract]");
    if (c.version !== "tcou-cart-1" || typeof c.allowed !== "boolean" || !Array.isArray(c.items) || c.items.some((i) => !/^([1-9][0-9]*)$/.test(i.product_id) || !/^([1-9][0-9]*)$/.test(i.variant_id) || !Number.isSafeInteger(i.quantity) || i.quantity < 1 || typeof i.key !== "string" || !i.key)) throw Error("Invalid cart contract");
    return c;
  };
  doc.addEventListener("submit", (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    let action;
    try {
      action = new URL(form.action, location.href);
    } catch {
      return;
    }
    if (action.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) {
      if (!ownedFormAllowed(form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      return;
    }
    if (e.submitter?.name !== "checkout" || !form.closest("[data-tcou-cart-surface]")) return;
    if (consented.has(form)) {
      consented.delete(form);
      return;
    }
    if (!termsAccepted(form.closest("[data-tcou-cart-surface]"))) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showCartError(form, "Please accept the store terms before continuing.");
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    const button = e.submitter;
    button.disabled = true;
    freshCart().then((c) => {
      if (c.allowed !== true || !termsAccepted(form.closest("[data-tcou-cart-surface]"))) throw Error("Cart or terms changed");
      if (!form.checkValidity()) {
        button.disabled = false;
        form.reportValidity();
        return;
      }
      consented.add(form);
      button.disabled = false;
      form.requestSubmit(button);
    }).catch(() => {
      button.disabled = false;
      showCartError(form);
    });
  }, true);
  doc.addEventListener("click", (e) => {
    const a = e.target.closest("[data-tcou-cart-surface] .checkout-buttons a");
    if (!a || a.hasAttribute("data-tcou-cart-recovery")) return;
    if (new URL(a.href).pathname === new URL(cartURL, location.origin).pathname) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!termsAccepted(a.closest("[data-tcou-cart-surface]"))) {
      showCartError(a, "Please accept the store terms before continuing.");
      return;
    }
    if (a.getAttribute("aria-busy") === "true") return;
    a.setAttribute("aria-busy", "true");
    freshCart().then((c) => {
      if (c.allowed !== true || !termsAccepted(a.closest("[data-tcou-cart-surface]"))) throw Error("Restricted cart or terms");
      location.assign(a.href);
    }).catch(() => showCartError(a)).finally(() => a.removeAttribute("aria-busy"));
  }, true);
  function showCartError(el, message = null) {
    let box = el.closest("[data-tcou-cart-surface]")?.querySelector("[data-tcou-checkout-error]");
    if (!box) {
      box = doc.createElement("p");
      box.dataset.tcouCheckoutError = "";
      box.setAttribute("role", "alert");
      el.closest("[data-tcou-cart-surface]")?.append(box);
    }
    box.textContent = message || "Checkout could not be confirmed. Review your cart and remove items that cannot be purchased here.";
  }
  async function mergeNativeCarts() {
    const forms = [...doc.querySelectorAll("cart-form[data-tcou-cart-surface]")];
    await Promise.all(forms.map(async (form) => {
      const section = form.dataset.sectionId;
      if (!section || typeof form.refreshFromHtml !== "function") throw Error("Native cart refresh not ready");
      const r = await fetch(contractURL(root, cartURL, section), { credentials: "same-origin", cache: "no-store" });
      if (!r.ok) throw Error("Cart view refresh failed");
      const html = await r.text();
      if (!html.includes("data-tcou-cart-surface")) throw Error("Unexpected cart section");
      form.refreshFromHtml(html);
    }));
  }
  async function mountOffers(widget) {
    if (mounts.has(widget)) return;
    mounts.add(widget);
    const status = widget.querySelector("[data-tcou-offer-status]"), list = widget.querySelector("[data-tcou-offer-list]");
    let candidates;
    try {
      candidates = JSON.parse(widget.querySelector("[data-tcou-offer-candidates]").textContent);
    } catch {
      return;
    }
    const paths = new Map(candidates.map((c) => [c.id, c.handle]));
    async function load(id, knownCart = null) {
      if (!paths.has(id)) return null;
      const cart = knownCart || await freshCart();
      if (cart.allowed !== true || cart.items.some((i) => "gid://shopify/Product/" + i.product_id === id)) return null;
      const url = contractURL(root, root + "products/" + encodeURIComponent(paths.get(id)), "tcou-product-contract");
      const product = await fetchContract(url, "[data-tcou-product-contract]");
      return product.version === "tcou-purchase-1" && product.id === id ? product : null;
    }
    try {
      const cart = await freshCart();
      const initial = candidates.filter((c) => !cart.items.some((i) => "gid://shopify/Product/" + i.product_id === c.id)).slice(0, 3);
      const products = (await Promise.all(initial.map((c) => load(c.id, cart).catch(() => null)))).filter((p) => p?.approved === true && p.mode === "Shopify" && p.externalEvidence === false && p.variants.some((v) => v.available === true && v.decision?.local_allowed === true));
      if (!widget.isConnected) return;
      if (!products.length) {
        widget.hidden = true;
        return;
      }
      const integration = createOrdinaryCartIntegration({
        transport: createAjaxCartTransport({ fetchImpl: fetch, root, writesEnabled: true }),
        loadCurrentProduct: load,
        approvedProductIDs: candidates.map((c) => c.id),
        onCart: async () => {
          await mergeNativeCarts();
          status.textContent = "Cart updated from Shopify.";
        }
      });
      mountQuantityCrossSell(list, products, integration, saved);
      for (const [i, article] of [...list.children].entries()) article.dataset.tcouOfferId = products[i].id;
    } catch {
      status.textContent = "Recommendations are temporarily unavailable. Your cart is unchanged.";
    }
  }
  function updateSticky() {
    for (const el of doc.querySelectorAll("[data-tcou-sticky]")) {
      const section = sectionFor(el), main = section.querySelector("[data-tcou-purchase]"), r = main?.getBoundingClientRect();
      const drawerOpen = !!doc.querySelector('.js-cart-drawer[open],.js-cart-drawer[aria-hidden="false"]');
      el.hidden = !r || r.bottom > 0 || drawerOpen || !!pending.get(section)?.busy;
    }
  }
  const scan = () => {
    doc.querySelectorAll("[data-tcou-offers]").forEach(mountOffers);
    updateSticky();
  };
  const observer = new MutationObserver(() => queueMicrotask(scan));
  observer.observe(doc.body, { childList: true, subtree: true });
  doc.addEventListener("on:cart-drawer:before-open", () => doc.querySelectorAll("[data-tcou-sticky]").forEach((el) => {
    el.hidden = true;
  }));
  doc.addEventListener("on:cart-drawer:after-open", updateSticky);
  doc.addEventListener("on:cart-drawer:after-close", updateSticky);
  doc.addEventListener("on:cart:after-merge", scan);
  window.addEventListener("scroll", updateSticky, { passive: true });
  window.addEventListener("resize", updateSticky);
  scan();
  return { disconnect() {
    observer.disconnect();
    window.removeEventListener("scroll", updateSticky);
    window.removeEventListener("resize", updateSticky);
  } };
}
if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot(), { once: true });
  else boot();
}
export {
  boot,
  contractURL,
  fetchContract,
  ownedFormAllowed,
  parseContract
};
